import { motion } from "framer-motion";
import { ArrowLeft, Code, Key, Book, Zap } from "lucide-react";
import { Button } from "../components/ui/button";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";

export default function APIPage() {
  const navigate = useNavigate();
  
  const endpoints = [
    { method: "POST", path: "/api/campaigns", description: "Create a new AI marketing campaign" },
    { method: "GET", path: "/api/campaigns", description: "List all your campaigns" },
    { method: "POST", path: "/api/text/command", description: "Send a text command to AI" },
    { method: "POST", path: "/api/voice/command", description: "Send a voice command to AI" },
    { method: "POST", path: "/api/safety/kill-switch", description: "Emergency stop all AI activities" },
  ];
  
  return (
    <div className="min-h-screen bg-background py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center gap-3 mb-6">
            <Code className="w-8 h-8 text-primary" />
            <h1 className="text-3xl font-bold">API Documentation</h1>
          </div>
          
          <p className="text-muted-foreground mb-8">
            Integrate YASNO's AI marketing capabilities directly into your applications with our RESTful API.
          </p>
          
          {/* API Key Section */}
          <Card className="glass-card border-white/10 mb-8">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <Key className="w-5 h-5 text-primary" />
                <h2 className="text-xl font-semibold">Authentication</h2>
              </div>
              <p className="text-muted-foreground mb-4">
                All API requests require authentication using a Bearer token. Include your token in the Authorization header:
              </p>
              <div className="bg-black/30 rounded-lg p-4 font-mono text-sm overflow-x-auto">
                <code>Authorization: Bearer YOUR_ACCESS_TOKEN</code>
              </div>
            </CardContent>
          </Card>
          
          {/* Endpoints Section */}
          <Card className="glass-card border-white/10 mb-8">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <Zap className="w-5 h-5 text-primary" />
                <h2 className="text-xl font-semibold">Endpoints</h2>
              </div>
              <div className="space-y-3">
                {endpoints.map((endpoint, index) => (
                  <div 
                    key={index}
                    className="flex items-center gap-4 p-3 bg-white/5 rounded-lg"
                  >
                    <span className={`text-xs font-bold px-2 py-1 rounded ${
                      endpoint.method === "GET" ? "bg-green-500/20 text-green-400" : "bg-blue-500/20 text-blue-400"
                    }`}>
                      {endpoint.method}
                    </span>
                    <code className="text-sm font-mono">{endpoint.path}</code>
                    <span className="text-sm text-muted-foreground ml-auto hidden md:block">
                      {endpoint.description}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          
          {/* Coming Soon */}
          <Card className="glass-card border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <Book className="w-5 h-5 text-primary" />
                <h2 className="text-xl font-semibold">Full Documentation Coming Soon</h2>
              </div>
              <p className="text-muted-foreground">
                We're working on comprehensive API documentation with code examples in multiple languages. 
                Contact us for early access to our developer program.
              </p>
              <Button className="mt-4" onClick={() => window.location.href = 'mailto:support@yasnomktg.com'}>
                Request API Access
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
